(() => {
  if (customElements.get('ff-color-family')) return

  const sizeIndexes = (product) => {
    if (!Array.isArray(product?.options) || product.options.length !== 2) return null
    const explicit = [Number(product.widthIndex), Number(product.lengthIndex)]
    if (product.widthIndex !== undefined && product.lengthIndex !== undefined &&
        explicit.every((index) => index === 0 || index === 1) && explicit[0] !== explicit[1]) return explicit
    const names = product.options.map((option) => String(option?.name ?? option)
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, ''))
    const width = names.findIndex((name) => /^(w|width|waist|waistwidth|waistsize|weite|bundweite|taille|tourdetaille|largeur)$/.test(name))
    const length = names.findIndex((name) => /^(l|length|inseam|inseamlength|lange|laenge|beinlange|beinlaenge|innenbeinlange|innenbeinlaenge|longueur)$/.test(name))
    return width >= 0 && length >= 0 && width !== length ? [width, length] : null
  }

  const exactVariant = (product, pair) => {
    const indexes = sizeIndexes(product)
    if (!indexes || !Array.isArray(product.variants)) return null
    return product.variants.find((variant) => Array.isArray(variant.options) && variant.options.length === 2 &&
      variant.options[indexes[0]] === pair[0] && variant.options[indexes[1]] === pair[1] &&
      /^\d{1,24}$/.test(String(variant.id))) || null
  }

  const productURL = (value) => {
    try {
      if (typeof value !== 'string' || value.length > 2048) return null
      const url = new URL(value, window.location.origin)
      return url.origin === window.location.origin && !url.username && !url.password &&
        /^\/(?:[a-z]{2,3}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname) ? url : null
    } catch {
      return null
    }
  }

  class FFColorFamily extends HTMLElement {
    constructor() {
      super()
      this.ffEpoch = 0
      this.ffOnSize = (event) => {
        if ((event.detail?.picker || event.target) === this.ffPicker()) this.ffSchedule()
      }
      this.ffOnChange = (event) => {
        if (this.ffPicker()?.contains(event.target)) this.ffSchedule()
      }
      this.ffOnVariant = (event) => {
        if (String(event.detail?.sectionId) !== String(this.dataset.sectionId)) return
        const picker = this.ffPicker()
        const source = event.detail?.formElement
        if (!picker || (source && source !== picker && !picker.contains(source))) return
        this.ffSchedule()
      }
      this.ffOnPopState = () => {
        this.ffNotice = this.ffDesiredNotice()
        this.ffSignature = undefined
        this.ffSchedule()
      }
    }

    connectedCallback() {
      if (this.ffConnected) return
      this.ffConnected = true
      this.ffEpoch += 1
      this.ffBases = new WeakMap()
      this.ffSignature = undefined
      this.ffStatus = this.querySelector('[data-ff-color-status]')
      try {
        this.ffData = JSON.parse(this.querySelector('[data-ff-color-data]')?.textContent || '{}')
      } catch {
        this.ffData = null
      }
      if (!this.ffData || !Array.isArray(this.ffData.members) || !sizeIndexes(this.ffData)) return
      this.ffMembers = new Map(this.ffData.members.map((member) => [String(member.id), member]))
      this.ffModal = this.closest('quick-add-modal, quick-view-modal')
      this.ffScope = this.ffModal || this.closest('.shopify-section') || this.parentElement
      if (!this.ffScope) return
      this.ffNotice = this.ffDesiredNotice()
      this.ffScope.addEventListener('change', this.ffOnChange)
      document.addEventListener('ff:size-state', this.ffOnSize)
      document.addEventListener('variant:change', this.ffOnVariant)
      window.addEventListener('popstate', this.ffOnPopState)
      this.ffObserver = new MutationObserver(() => this.ffSchedule())
      this.ffObserver.observe(this.ffScope, {childList: true, subtree: true})
      this.ffSchedule()
    }

    disconnectedCallback() {
      this.ffConnected = false
      this.ffEpoch += 1
      this.ffScheduled = false
      this.ffScope?.removeEventListener('change', this.ffOnChange)
      document.removeEventListener('ff:size-state', this.ffOnSize)
      document.removeEventListener('variant:change', this.ffOnVariant)
      window.removeEventListener('popstate', this.ffOnPopState)
      this.ffObserver?.disconnect()
    }

    ffPicker() {
      if (!this.ffScope) return null
      const candidates = Array.from(this.ffScope.querySelectorAll(
        'variant-selects[data-ff-explicit-size="true"], variant-radios[data-ff-explicit-size="true"]'
      )).filter((picker) => String(picker.dataset.section) === String(this.dataset.sectionId) &&
        picker.closest('quick-add-modal, quick-view-modal') === this.ffModal)
      return candidates.length === 1 ? candidates[0] : null
    }

    ffSelection() {
      const picker = this.ffPicker()
      if (!picker) return [null, null]
      const source = {...this.ffData}
      if (picker.dataset.ffWidthIndex !== undefined && picker.dataset.ffLengthIndex !== undefined) {
        source.widthIndex = picker.dataset.ffWidthIndex
        source.lengthIndex = picker.dataset.ffLengthIndex
      }
      const indexes = sizeIndexes(source)
      if (!indexes) return [null, null]
      const values = [null, null]
      const groups = Array.from(picker.querySelectorAll('fieldset[data-ff-option-index], .product-form__input[data-ff-option-index]'))
      for (const index of indexes) {
        const matching = groups.filter((group) => Number(group.dataset.ffOptionIndex) === index)
        if (matching.length !== 1) continue
        const radios = matching[0].querySelectorAll('input[type="radio"]:checked')
        const value = radios.length === 1 ? radios[0].value : radios.length ? null : matching[0].querySelector('select')?.value
        if (typeof value === 'string' && value !== '') values[index] = value
      }
      return indexes.map((index) => values[index])
    }

    ffDesiredNotice() {
      if (this.ffModal) return null
      const current = this.ffMembers.get(String(this.ffData.currentProductId))
      const currentURL = productURL(current?.url)
      if (!currentURL || currentURL.pathname !== window.location.pathname) return null
      const params = new URL(window.location.href).searchParams
      if (params.has('variant')) return null
      const pair = ['ff_w', 'ff_l'].map((name) => {
        const values = params.getAll(name)
        const value = values.length === 1 ? values[0] : ''
        return value && value.length <= 32 && !/[\u0000-\u001f\u007f]/.test(value) ? value : null
      })
      return pair.every(Boolean) && !exactVariant(this.ffData, pair) ? pair : null
    }

    ffBase(link, member) {
      const cached = this.ffBases.get(link)
      if (cached?.id === String(member.id)) return new URL(cached.href)
      const base = productURL(member.url)
      if (!base) return null
      const original = productURL(link.getAttribute('href'))
      const url = original?.pathname === base.pathname ? original : base
      url.searchParams.delete('variant')
      url.searchParams.delete('ff_w')
      url.searchParams.delete('ff_l')
      this.ffBases.set(link, {id: String(member.id), href: url.href})
      return url
    }

    ffSchedule() {
      if (this.ffScheduled || !this.ffConnected || !this.ffData || !this.ffScope) return
      this.ffScheduled = true
      const epoch = this.ffEpoch
      queueMicrotask(() => {
        if (!this.ffConnected || epoch !== this.ffEpoch) return
        this.ffScheduled = false
        this.ffRefresh()
      })
    }

    ffRefresh() {
      const pair = this.ffSelection()
      const signature = JSON.stringify(pair)
      if (this.ffSignature !== undefined && this.ffSignature !== signature) this.ffNotice = null
      this.ffSignature = signature
      const complete = pair.every(Boolean)
      this.querySelectorAll('a[data-ff-color-link][data-product-id]').forEach((link) => {
        if (link.closest('ff-color-family') !== this) return
        const member = this.ffMembers.get(String(link.dataset.productId))
        if (!member) return
        const url = this.ffBase(link, member)
        if (!url) return
        let state = 'empty'
        if (complete && sizeIndexes(member)) {
          const variant = exactVariant(member, pair)
          if (variant) {
            url.searchParams.set('variant', String(variant.id))
            state = variant.available ? 'available' : 'soldout'
          } else if (pair.every((value) => value.length <= 32 && !/[\u0000-\u001f\u007f]/.test(value))) {
            url.searchParams.set('ff_w', pair[0])
            url.searchParams.set('ff_l', pair[1])
            state = 'unavailable'
          }
        }
        link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`)
        link.dataset.ffColorSizeState = state
        const availability = link.querySelector('[data-ff-color-availability]')
        if (availability) {
          const key = complete ? state === 'soldout' ? 'sizeSoldOut' : state === 'unavailable' ? 'sizeUnavailable' : null :
            member.available === false ? 'productSoldOut' : null
          const label = key && typeof this.ffData.strings?.[key] === 'string' ? this.ffData.strings[key] : ''
          if (availability.textContent !== label) availability.textContent = label
          availability.hidden = !label
        }
      })
      if (this.ffStatus) {
        const currentVariant = complete ? exactVariant(this.ffData, pair) : null
        const soldOut = currentVariant && !currentVariant.available
        const messagePair = soldOut ? pair : this.ffNotice
        const template = soldOut ? this.ffData.strings?.soldOut : this.ffData.strings?.unavailable
        const text = typeof template === 'string' && messagePair ? template
          .replace(/\[width\]/g, () => messagePair[0]).replace(/\[length\]/g, () => messagePair[1]) : ''
        const visible = Boolean(text && (soldOut || (this.ffNotice && pair.every((value) => !value))))
        if (this.ffStatus.textContent !== text) this.ffStatus.textContent = text
        this.ffStatus.hidden = !visible
      }
    }
  }

  customElements.define('ff-color-family', FFColorFamily)
})()
